import React from 'react'
import { connect } from 'react-redux'
import { fetchBackfillStatus, switchBackfill, dismissBackfillError } from 'actions/backFillActions'
import moment from 'moment-timezone'
import _ from 'lodash'
import { localStoragePayhelm } from 'modules/utils'
import { ChevronLeft, ChevronRight, AlertTriangle, X } from 'lucide-react'
import { Popover, PopoverContent, PopoverTrigger } from '@/features/shadcn/ui/popover'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/features/shadcn/ui/sidebar'
import mockBackfillData from './mock-data.json'

const clampPercent = (value) => Math.max(0, Math.min(100, Number.isFinite(value) ? value : 0))
const PopoverContentAny = PopoverContent as any

const formatProgress = (current = 0, total = 0) => {
  if (!total) return 0
  return clampPercent(Math.floor((current / total) * 100))
}

const formatLastUpdated = (updatedAt, timezone) => {
  if (!updatedAt) return null
  return moment.tz(updatedAt, timezone).format('h:mmA, MMM D z')
}

const statusLabelMap = {
  running: 'Backfill in progress',
  done: 'Backfill complete',
  error: 'Backfill failed',
}

const statusToneMap = {
  running: 'tw-text-slate-600',
  done: 'tw-text-green-600',
  error: 'tw-text-red-600',
}

const isMockModeEnabled = () => {
  if (process.env.NODE_ENV !== 'development') return false
  if (typeof window === 'undefined') return false
  return window.localStorage.getItem('payhelm.mockBackfillBanner') === '1'
}

const mapStateToProps = (store) => {
  const mockMode = isMockModeEnabled()
  const index = store.backfill.index
  const accounts = store.root.accounts
  const sourceBackfills = mockMode ? (mockBackfillData as any[]) : store.backfill.backfill
  const activeBackfills = sourceBackfills
    .filter((b) => b && b.id && (b.status === 'running' || b.status === 'error' || b.status === 'done'))
    .map(b => {
    const account = accounts.find(a => a.merchant_id === b.merchant_id) || {}
    return {
      ...b,
      email: b.email || account.email,
      channel: b.channel || account.channel,
    }
  })
  const backfill = activeBackfills[index] || activeBackfills[0]
  const length = activeBackfills.length
  const timezone = _.get(store, ['root', 'user', 'default_timezone'], 'utc')
  return { length, backfill, index, accounts, timezone, mockMode }
}

const isSuspensionError = (msg) => typeof msg === 'string' && msg.toLowerCase().includes('has been suspended')

const mapDispatchToProps = { fetchBackfillStatus, switchBackfill, dismissBackfillError }

class BackfillBanner extends React.Component<any, any> {
  constructor(props) {
    super(props)
    this.state = {
      fadeIn: false,
      popoverOpen: false,
    }
  }

  composeDateRange = (isPayPal, created_at, current, total, timezone, start_date, end_date) => {
    const period = isPayPal ? 'months' : 'days'
    const format = isPayPal ? 'MMM YYYY' : 'MMM D, YYYY'
    if (start_date && end_date) {
      return {
        currentAt: moment.tz(end_date, timezone).subtract(current, period).format(format),
        from: moment.tz(start_date, timezone).format(format),
        to: moment.tz(end_date, timezone).format(format),
      }
    }
    else {
      return {
        currentAt: moment.tz(created_at, timezone).subtract(current, period).format(format),
        from: moment.tz(created_at, timezone).subtract(total, period).format(format),
        to: moment.tz(created_at, timezone).format(format),
      }
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.index >= this.props.length) {
      this.props.switchBackfill(0)
    }

    if (!prevProps.backfill || !this.props.backfill) return

    if (
      (prevProps.backfill.merchant_id === this.props.backfill.merchant_id) &&
      (prevProps.backfill.current !== this.props.backfill.current)
    ) {
      this.setState({
        fadeIn: true
      }, () => setTimeout(() => this.setState({ fadeIn: false }), 100))
    }
  }

  componentDidMount() {
    const { fetchBackfillStatus, accounts, mockMode } = this.props
    if (mockMode) return
    delete (localStoragePayhelm as any).backfillToken
    fetchBackfillStatus('', (accounts || []).map(a => a.merchant_id))
  }

  handleSwitchBackfill = (direction) => {
    const { index, length, switchBackfill } = this.props
    if (length <= 1) return
    const nextIndex = (index + length + direction) % length
    switchBackfill(nextIndex)
  }

  renderSummary = ({
    collapsedView,
    isError,
    statusLabel,
    status_message,
    progress,
    status,
    current_orders,
    total_orders,
  }) => {
    if (collapsedView) {
      if (isError) {
        return (
          <div className="tw-flex tw-flex-col tw-items-center tw-gap-1">
            <AlertTriangle className="tw-h-4 tw-w-4 tw-text-red-500" />
          </div>
        )
      }
      return (
        <div className="tw-flex tw-flex-col tw-items-center tw-gap-1">
          <span className="tw-text-[10px] tw-font-semibold tw-text-slate-700">{Math.round(progress)}%</span>
          <div className="tw-h-1 tw-w-full tw-overflow-hidden tw-rounded-sm tw-bg-slate-200">
            <div
              className="tw-h-full tw-rounded-sm tw-transition-all tw-duration-150"
              style={{ width: `${progress}%`, background: 'linear-gradient(90deg, #3366FF, #5588FF)' }}
            />
          </div>
        </div>
      )
    }

    if (isError) {
      return (
        <>
          <div className="tw-flex tw-items-center tw-gap-1.5 tw-mb-1">
            <AlertTriangle className="tw-h-3.5 tw-w-3.5 tw-text-red-500 tw-flex-shrink-0" />
            <span className="tw-text-xs tw-font-semibold tw-text-red-700">{statusLabel}</span>
          </div>
          <div className="tw-text-[11px] tw-text-red-600 tw-text-left tw-line-clamp-2">
            {status_message || 'An error occurred during the backfill.'}
          </div>
        </>
      )
    }

    return (
      <>
        <div className="tw-mb-1.5 tw-flex tw-items-center tw-justify-between">
          <span className="tw-text-xs tw-font-semibold tw-text-slate-800">{statusLabel}</span>
          <span className="tw-text-[11px] tw-text-slate-500">{Math.round(progress)}%</span>
        </div>
        <div className="tw-h-1.5 tw-overflow-hidden tw-rounded-sm tw-bg-slate-200">
          <div
            className="tw-h-full tw-rounded-sm tw-transition-all tw-duration-150"
            style={{ width: `${progress}%`, background: 'linear-gradient(90deg, #3366FF, #5588FF)' }}
          />
        </div>
        <div className="tw-mt-1.5 tw-text-[11px] tw-text-slate-500 tw-text-left">
          {status === 'done'
            ? `Synced orders · ${total_orders || current_orders}`
            : `Syncing orders · ${current_orders || 0} / ${total_orders || 0}`}
        </div>
      </>
    )
  }

  renderDetails = ({
    mobile,
    displayedName,
    status,
    statusLabel,
    hasMultiple,
    isError,
    isSuspension,
    status_message,
    backfillId,
    progress,
    current_orders,
    total_orders,
    from,
    to,
    currentAt,
    lastUpdated,
    fadeIn,
  }) => {
    const iconBtnClass = mobile
      ? 'tw-inline-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-md tw-border tw-border-slate-200 tw-text-slate-600 hover:tw-bg-slate-50'
      : 'tw-inline-flex tw-h-7 tw-w-7 tw-items-center tw-justify-center tw-rounded-md tw-border tw-border-slate-200 tw-text-slate-600 hover:tw-bg-slate-50'

    return (
      <>
        <div className="tw-flex tw-items-start tw-justify-between tw-gap-3">
          <div className="tw-min-w-0">
            <div className="tw-text-sm tw-font-semibold tw-text-slate-900 tw-break-words">{displayedName || 'Backfill'}</div>
            <div className={`tw-text-xs ${statusToneMap[status] || 'tw-text-slate-600'}`}>{statusLabel}</div>
          </div>
          <div className="tw-flex tw-flex-shrink-0 tw-items-center tw-gap-1">
            {hasMultiple && (
              <>
                <button
                  type="button"
                  className={iconBtnClass}
                  onClick={() => this.handleSwitchBackfill(-1)}
                  aria-label="Show previous backfill"
                >
                  <ChevronLeft className="tw-h-4 tw-w-4" />
                </button>
                <button
                  type="button"
                  className={iconBtnClass}
                  onClick={() => this.handleSwitchBackfill(1)}
                  aria-label="Show next backfill"
                >
                  <ChevronRight className="tw-h-4 tw-w-4" />
                </button>
              </>
            )}
            {isError && (
              <button
                type="button"
                className={iconBtnClass}
                onClick={(e) => {
                  e.stopPropagation()
                  this.setState({ popoverOpen: false })
                  this.props.dismissBackfillError(backfillId)
                }}
                aria-label="Dismiss error"
              >
                <X className="tw-h-4 tw-w-4" />
              </button>
            )}
          </div>
        </div>

        {isError ? (
          <>
            <div className="tw-mt-3 tw-rounded-md tw-bg-red-50 tw-border tw-border-red-200 tw-p-2.5">
              <p className="tw-text-xs tw-text-red-700 tw-break-words">
                {status_message || 'An error occurred during the backfill.'}
              </p>
              {isSuspension && (
                <p className="tw-text-xs tw-text-red-600 tw-mt-1.5">
                  Please resolve the suspension with your platform before retrying.
                </p>
              )}
            </div>
            <div className="tw-mt-3 tw-flex tw-gap-2">
              <button
                type="button"
                className="tw-flex-1 tw-rounded-md tw-border tw-border-slate-200 tw-bg-white tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-slate-700 hover:tw-bg-slate-50"
                onClick={(e) => {
                  e.stopPropagation()
                  this.setState({ popoverOpen: false })
                  this.props.dismissBackfillError(backfillId)
                }}
              >
                Dismiss
              </button>
              {!isSuspension && (
                <a
                  href="/settings/integrations"
                  className="tw-flex-1 tw-rounded-md tw-bg-blue-600 tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-white tw-text-center hover:tw-bg-blue-700 tw-no-underline"
                  onClick={(e) => {
                    e.stopPropagation()
                    this.setState({ popoverOpen: false })
                  }}
                >
                  Retry
                </a>
              )}
            </div>
          </>
        ) : (
          <>
            <div className="tw-mt-3 tw-h-2 tw-overflow-hidden tw-rounded-sm tw-bg-slate-200">
              <div
                className="tw-h-full tw-rounded-sm tw-transition-all tw-duration-150"
                style={{ width: `${progress}%`, background: 'linear-gradient(90deg, #3366FF, #5588FF)' }}
              />
            </div>

            <div className="tw-mt-2 tw-text-xs tw-text-slate-600">
              {status === 'done'
                ? 'Backfill has completed successfully.'
                : `Processed order(s): ${current_orders || 0}/${total_orders || 0}`}
            </div>

            <div className="tw-mt-3 tw-space-y-1 tw-text-xs tw-text-slate-600 tw-break-words">
              <div>{`Window: ${from} -> ${to}`}</div>
              <div>{`Current period: `}
                <span className={`fade-in-${fadeIn ? 'start' : 'end'}`}>{currentAt}</span>
              </div>
              {lastUpdated ? <div>{`Last updated: ${lastUpdated}`}</div> : null}
              {status_message ? <div>{status_message}</div> : null}
            </div>
          </>
        )}
      </>
    )
  }

  render() {
    const { fadeIn, popoverOpen } = this.state
    const { length, backfill, timezone, style, collapsed, isMobile } = this.props
    if (!backfill) return null

    const {
      status = 'running',
      current = 0,
      total = 1,
      current_period_orders_total: total_orders = 0,
      current_period_orders_processed: current_orders = 0,
      merchant_id = '',
      channel = '',
      email = '',
      created_at = new Date(),
      updated_at,
      start_date,
      end_date,
      status_message,
    } = backfill

    const isPayPal = channel.toLowerCase().includes('paypal')
    const displayedName = isPayPal ? `${email} (PayPal)` : merchant_id
    const hasMultiple = length > 1
    const isError = status === 'error'
    const isSuspension = isError && isSuspensionError(status_message)
    const percentage = formatProgress(current, total)
    const percentage_orders = formatProgress(current_orders, total_orders)
    const progress = status === 'done' ? 100 : (percentage_orders || percentage)
    const statusLabel = statusLabelMap[status] || statusLabelMap.running
    const collapsedView = Boolean(collapsed)
    const { currentAt, from, to } = this.composeDateRange(isPayPal, created_at, current, total, timezone, start_date, end_date)
    const lastUpdated = formatLastUpdated(updated_at, timezone)

    const summaryProps = {
      collapsedView,
      isError,
      statusLabel,
      status_message,
      progress,
      status,
      current_orders,
      total_orders,
    }

    const detailsProps = {
      displayedName,
      status,
      statusLabel,
      hasMultiple,
      isError,
      isSuspension,
      status_message,
      backfillId: backfill.id,
      progress,
      current_orders,
      total_orders,
      from,
      to,
      currentAt,
      lastUpdated,
      fadeIn,
    }

    const summaryButton = (
      <button
        type="button"
        className={`tw-rounded-md tw-border ${isError ? 'tw-border-red-200 tw-bg-red-50 hover:tw-bg-red-100' : 'tw-border-slate-200 tw-bg-[#f8f9fb] hover:tw-bg-slate-50'} tw-transition-colors ${collapsedView ? 'tw-w-12 tw-p-2' : 'tw-flex-1 tw-p-3'}`}
        aria-label="Open backfill details"
      >
        {this.renderSummary(summaryProps)}
      </button>
    )

    const navArrows = hasMultiple && (
      <>
        <button
          type="button"
          className="tw-h-auto tw-min-h-[52px] tw-w-7 tw-rounded-md tw-border tw-border-slate-200 tw-bg-white tw-text-slate-500 hover:tw-bg-slate-50 hover:tw-text-slate-700"
          onClick={() => this.handleSwitchBackfill(-1)}
          aria-label="Show previous backfill"
        >
          <ChevronLeft className="tw-h-4 tw-w-4 tw-mx-auto" />
        </button>
      </>
    )

    const navArrowsNext = hasMultiple && (
      <button
        type="button"
        className="tw-h-auto tw-min-h-[52px] tw-w-7 tw-rounded-md tw-border tw-border-slate-200 tw-bg-white tw-text-slate-500 hover:tw-bg-slate-50 hover:tw-text-slate-700"
        onClick={() => this.handleSwitchBackfill(1)}
        aria-label="Show next backfill"
      >
        <ChevronRight className="tw-h-4 tw-w-4 tw-mx-auto" />
      </button>
    )

    if (isMobile) {
      return (
        <div className="tw-w-full" style={style}>
          {/* On mobile the sidebar is a Radix Sheet. A portal'd Popover inside a Sheet
              clips off-screen (side=right) and can fight the Sheet's dismiss layer.
              Inline Collapsible expands upward in-place instead. */}
          <Collapsible
            open={popoverOpen}
            onOpenChange={(open) => this.setState({ popoverOpen: open })}
            className="tw-flex tw-w-full tw-flex-col-reverse"
          >
            <div className="tw-flex tw-items-stretch tw-gap-1">
              {navArrows}
              <CollapsibleTrigger asChild>
                {summaryButton}
              </CollapsibleTrigger>
              {navArrowsNext}
            </div>
            <CollapsibleContent>
              <div className="tw-mb-1.5 tw-w-full tw-rounded-md tw-border tw-border-slate-200 tw-bg-white tw-p-3">
                {this.renderDetails({ ...detailsProps, mobile: true })}
              </div>
            </CollapsibleContent>
          </Collapsible>
        </div>
      )
    }

    return (
      <div className="tw-w-full" style={style}>
        <Popover open={popoverOpen} onOpenChange={(open) => this.setState({ popoverOpen: open })}>
          <div className="tw-flex tw-items-stretch tw-gap-1">
            {navArrows}
            <PopoverTrigger asChild>
              {summaryButton}
            </PopoverTrigger>
            {navArrowsNext}
          </div>
          <PopoverContentAny side="right" align="start" className="tw-w-80 tw-p-3">
            {this.renderDetails({ ...detailsProps, mobile: false })}
          </PopoverContentAny>
        </Popover>
      </div>
    )
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(BackfillBanner)
